/**
 * Privacy / Terms / Support — minimal static pages, hash-routed (#privacy, #terms, #support).
 */
import React from 'react'
import { MANAGE_SUBSCRIPTION_URL, SUBSCRIBE } from './catalogue'

const wrap: React.CSSProperties = {
  position: 'fixed', inset: 0, overflowY: 'auto', background: '#06060a', color: '#ddd',
  fontFamily: 'monospace', fontSize: 13.5, lineHeight: 1.7
}
const inner: React.CSSProperties = { maxWidth: 680, margin: '0 auto', padding: '48px 22px 80px' }
const h: React.CSSProperties = { fontFamily: "'Changa', sans-serif", fontWeight: 700, color: '#fff', letterSpacing: 1 }

function Shell({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={wrap}>
      <div style={inner}>
        <a href="#" style={{ color: '#9b86ff', textDecoration: 'none' }}>← back to SYQEL Art</a>
        <h1 style={{ ...h, fontSize: 30, margin: '18px 0 6px' }}>{title}</h1>
        <div style={{ opacity: 0.5, fontSize: 11, marginBottom: 26 }}>SYQEL® · © SYQEL INC · last updated June 2026</div>
        {children}
      </div>
    </div>
  )
}

export function Privacy(): JSX.Element {
  return (
    <Shell title="Privacy">
      <p><b style={h}>Your audio never leaves your device.</b> SYQEL analyses the music in your browser, locally. We do not record, upload, store or transmit your audio or what you listen to.</p>
      <p><b style={h}>No account is required.</b> We don't collect names, emails or profiles to use the player. Your free-preview usage is kept in your own browser's local storage.</p>
      <p><b style={h}>Payments are handled by Stripe.</b> When you subscribe, Stripe processes your payment and stores your billing details under its own privacy policy; we never see your card number.</p>
      <p><b style={h}>Hosting and basic analytics.</b> The site is served by Cloudflare, which processes requests (IP addresses, user agent) to deliver and protect the service. We use Google Analytics to understand how the player is used (pages viewed, buttons clicked, anonymous usage and purchase events) — never your audio, and never the content you listen to.</p>
      <p>Questions: <a href="mailto:support@syqel.com" style={{ color: '#9b86ff' }}>support@syqel.com</a></p>
    </Shell>
  )
}

export function Terms(): JSX.Element {
  return (
    <Shell title="Terms of Service">
      <p>SYQEL® Art is an audio-reactive visual experience provided by <b>SYQEL INC</b>. By using syqel.art you agree to these terms.</p>
      <p><b style={h}>A separate experience.</b> SYQEL Art is an independent service. It is separate from — and a SYQEL Art subscription does not include access to — SYQEL Desktop, SYQEL VJ, SYQEL Spatial or any other SYQEL INC product.</p>
      <p><b style={h}>License.</b> Your subscription grants a personal, non-transferable license to use SYQEL for personal viewing and performance. The visuals, worlds, engine and the SYQEL® mark remain the property of SYQEL INC.</p>
      <p><b style={h}>Subscription.</b> {SUBSCRIBE.monthly.label} or {SUBSCRIBE.annual.label}, billed via Stripe. Cancel anytime — <a href={MANAGE_SUBSCRIPTION_URL} style={{ color: '#9b86ff' }}>manage your subscription</a>; access continues to the end of the paid period.</p>
      <p><b style={h}>Acceptable use.</b> Don't redistribute, resell, reverse-engineer or rebroadcast the service or its visuals without written permission.</p>
      <p><b style={h}>No warranty.</b> The service is provided "as is", without warranties of any kind. To the maximum extent permitted by law, SYQEL INC's liability is limited to the amount you paid in the last 12 months.</p>
      <p>Questions: <a href="mailto:support@syqel.com" style={{ color: '#9b86ff' }}>support@syqel.com</a></p>
    </Shell>
  )
}

export function Support(): JSX.Element {
  return (
    <Shell title="Support">
      <p><b style={h}>Email.</b> <a href="mailto:support@syqel.com" style={{ color: '#9b86ff' }}>support@syqel.com</a> — we read everything.</p>
      <p><b style={h}>Manage your subscription.</b> <a href={MANAGE_SUBSCRIPTION_URL} style={{ color: '#9b86ff' }}>Update billing or cancel</a>.</p>
      <p><b style={h}>Quick fixes.</b> No sound? Make sure the tab isn't muted and, when capturing live audio, tick "share audio" in the picker. Choppy visuals? Close other GPU-heavy tabs/apps — SYQEL renders on your graphics card.</p>
      <p><b style={h}>Requests.</b> Want a world that doesn't exist yet? Tell us — new worlds ship constantly.</p>
    </Shell>
  )
}
